import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  CONTACT_RELATION_TYPES,
  CONTACT_RELATION_TYPE_LABELS,
  type ContactRelationInput,
  type ContactRelationType,
} from '@aimmo/contracts';
import {
  Button,
  Input,
  RelationPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@aimmo/ui';
import { resolveContact, searchContacts } from './contact-lookups.js';

export interface ContactRelationsFieldProps {
  value: unknown;
  onChange: (value: ContactRelationInput[]) => void;
  disabled: boolean;
}

/**
 * The relations this contact owns (U4).
 *
 * Here the type *is* a choice — a row means nothing without knowing whether it
 * is a spouse or a business partner — which is the opposite of the recapiti,
 * where the block already says it.
 *
 * Only one side of a relation is stored. What the other contact reads is the
 * inverse of what is entered here, computed at read time, so the far side never
 * has a row of its own to fall out of step.
 */
export function ContactRelationsField({ value, onChange, disabled }: ContactRelationsFieldProps) {
  const relations = React.useMemo(
    () => (Array.isArray(value) ? (value as ContactRelationInput[]) : []),
    [value],
  );

  function edit(index: number, patch: Partial<ContactRelationInput>): void {
    onChange(relations.map((relation, i) => (i === index ? { ...relation, ...patch } : relation)));
  }

  return (
    <div className="grid gap-2" data-slot="contact-relations">
      {relations.length === 0 && (
        <p className="text-secondary text-muted">Nessuna relazione registrata.</p>
      )}

      {relations.map((relation, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <Select
            value={relation.type}
            disabled={disabled}
            onValueChange={(next) => edit(index, { type: next as ContactRelationType })}
          >
            <SelectTrigger className="w-44" aria-label={`Tipo relazione ${index + 1}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONTACT_RELATION_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {CONTACT_RELATION_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="min-w-56 flex-1">
            <RelationPicker
              value={relation.relatedContactId || null}
              onChange={(next) => edit(index, { relatedContactId: next ?? '' })}
              search={searchContacts}
              resolve={resolveContact}
              placeholder="Contatto"
              aria-label={`Contatto relazione ${index + 1}`}
              disabled={disabled}
            />
          </div>

          <Input
            className="w-40"
            placeholder="note"
            aria-label={`Note relazione ${index + 1}`}
            disabled={disabled}
            value={relation.notes ?? ''}
            onChange={(event) => edit(index, { notes: event.target.value })}
          />

          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Rimuovi relazione"
            disabled={disabled}
            onClick={() => onChange(relations.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...relations, { type: 'coniuge', relatedContactId: '' }])}
        >
          <Plus />
          Aggiungi relazione
        </Button>
      </div>
    </div>
  );
}
